/**
 * EXPERIMENTAL — DO NOT COPY.
 *
 * Machine enrollment and machine authentication for one internal account. This
 * code has not been through a full security review. It is safe only because
 * `machineFrontDoor.ts` (shared secret + source-IP allowlist) and the Statsig gate
 * run before any of it. Do not reuse its patterns — codes, credential hashing,
 * the confirmation flow, socket auth — for any other auth surface. Start from
 * the reviewed session auth in `api/src/security/` instead.
 */

import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { api } from "@/lib/http.ts";
import type {
  ConfirmMachineResp,
  MachineRosterResp,
  MintConnectionCodeResp,
} from "@/types/userMachines.ts";

const COMPUTERS_KEY = ["computers"] as const;

/** How often the roster is re-read while a machine is part-way through connecting. */
const CONNECTING_POLL_MS = 5_000;

/**
 * The machines on this account. Read on the Computers settings tab.
 *
 * **Polled while a connection is in flight**, because the row the user is
 * waiting for is written by a different computer. They take a code to that
 * machine, run the program there, and the pending row — with the input for the
 * code it displayed — appears only on the next read. Nothing on this page
 * causes that read, so without the poll the user is left on a page that will
 * never change until they reload it.
 *
 * `connecting` is the caller's half of the condition: a code has been minted and
 * the machine has not shown up yet. The other half is read off the data, so the
 * poll keeps running while a machine is pending and stops once every machine has
 * settled — a lapsed pending row reads as `expired`, which is what ends it.
 */
export const computerQueries = {
  list: ({ connecting = false }: { connecting?: boolean } = {}) =>
    queryOptions({
      queryKey: COMPUTERS_KEY,
      queryFn: async ({ signal }) => {
        const { data } = await api.get<MachineRosterResp>("/api/a/machines", { signal });
        return data;
      },
      refetchInterval: (query) => {
        const waiting =
          connecting || (query.state.data?.machines.some((m) => m.status === "pending") ?? false);
        return waiting ? CONNECTING_POLL_MS : false;
      },
    }),
};

/**
 * Mint the connection code the user carries to the machine.
 *
 * **The code is handed back and deliberately not cached.** It is a live
 * capability for fifteen minutes, and a query cache is a second place holding
 * it with a lifetime nobody chose — so the component keeps it in state and
 * drops it on unmount, the same reasoning `ComputerDesktopBlock` records for
 * the desktop link.
 */
export const useMintConnectionCode = () =>
  useMutation({
    mutationFn: async () => {
      const { data } = await api.post<MintConnectionCodeResp>("/api/a/machines/code");
      return data;
    },
    onError: () => {
      errorToast("Couldn't create a connection code. Please try again.");
    },
  });

/** Confirm a pending machine with the code it displayed. */
export const useConfirmMachine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      machineId,
      confirmationCode,
    }: {
      machineId: number;
      confirmationCode: string;
    }) => {
      const { data } = await api.post<ConfirmMachineResp>(`/api/a/machines/${machineId}/confirm`, {
        confirmationCode,
      });
      return data;
    },
    onSuccess: async () => {
      confirmationToast("That computer is connected.");
      await queryClient.invalidateQueries({ queryKey: COMPUTERS_KEY });
    },
    // No toast here. The caller renders the refusal beside the input, because
    // the useful thing to do with a wrong code is correct it in place.
  });
};

/** Disconnect a machine: revoke its credential and close its sockets. */
export const useDisconnectMachine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (machineId: number) => {
      await api.post(`/api/a/machines/${machineId}/disconnect`);
    },
    onSuccess: async () => {
      confirmationToast("That computer was disconnected.");
      await queryClient.invalidateQueries({ queryKey: COMPUTERS_KEY });
    },
    onError: () => {
      errorToast("Couldn't disconnect that computer. Please try again.");
    },
  });
};
